import React, { useEffect, useState } from 'react';
import { storyPointAPI } from '../../../api/services';
import { useAppSelector } from '../../../store/hooks';

interface Props {
  projectId: string;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const trophyColors = ['text-yellow-500', 'text-gray-400', 'text-amber-600'];

const Leaderboard: React.FC<Props> = ({ projectId }) => {
  const { user } = useAppSelector(state => state.auth);
  const currentUserId = user?.id || user?._id;

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [taskSummary, setTaskSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const [res, summaryRes] = await Promise.all([
        storyPointAPI.getTeamPoints(projectId, selectedMonth, selectedYear),
        storyPointAPI.getTeamTaskSummary(projectId),
      ]);
      setLeaderboard(res.data?.data || res.data || []);
      setTaskSummary(summaryRes.data?.data || null);
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, [projectId, selectedMonth, selectedYear]);

  const maxPoints = leaderboard.length > 0
    ? Math.max(...leaderboard.map((e: any) => e.totalPoints || e.points || 0))
    : 0;

  const years: number[] = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) years.push(y);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Leaderboard</h2>
        <div className="flex items-center gap-2">
          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {MONTHS.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {leaderboard.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-8">
          <div className="w-20 h-20 rounded-2xl bg-gray-50 flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0016.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.023 6.023 0 01-3.52 1.122 6.023 6.023 0 01-3.52-1.122" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No data for this period</h2>
          <p className="text-sm text-gray-500 max-w-sm">
            Story point entries for {MONTHS[selectedMonth - 1]} {selectedYear} will appear here once team members log their work.
          </p>
        </div>
      ) : (
        <>
        {/* Project Stats */}
        {taskSummary && (
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
              <div className="text-2xl font-bold text-gray-900">{taskSummary.totalSP || 0}</div>
              <div className="text-xs text-gray-500">Total SP</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{taskSummary.approvedFeatures || 0}</div>
              <div className="text-xs text-gray-500">Approved Features</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{taskSummary.totalTasks || 0}</div>
              <div className="text-xs text-gray-500">Total Tasks</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
              <div className="text-2xl font-bold text-gray-900">{taskSummary.members?.length || 0}</div>
              <div className="text-xs text-gray-500">Team Members</div>
            </div>
          </div>
        )}

        {/* Team Members Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-medium text-gray-600">Member</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Role</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Tasks</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Earned</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Unclaimed</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Remaining</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Total</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 w-32">Progress</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(taskSummary?.members || []).map((m: any, idx: number) => {
                const earnedPct = m.totalSP > 0 ? Math.round((m.earnedSP / m.totalSP) * 100) : 0;
                const isMe = m.userId === currentUserId;
                return (
                  <tr key={m.userId} className={`${isMe ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {idx < 3 && m.totalSP > 0 ? (
                          <svg className={`w-4 h-4 ${trophyColors[idx]}`} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172" />
                          </svg>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center">{m.name[0]}</div>
                        )}
                        <span className="font-medium text-gray-900">{m.name}</span>
                        {isMe && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">You</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 capitalize">{m.role}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{m.completedTasks}/{m.totalTasks}</td>
                    <td className="px-4 py-3 text-right font-medium text-green-600">{m.earnedSP}</td>
                    <td className="px-4 py-3 text-right text-yellow-600">{m.unclaimedSP || 0}</td>
                    <td className="px-4 py-3 text-right text-gray-400">{m.remainingSP}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{m.totalSP}</td>
                    <td className="px-4 py-3">
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full transition-all ${earnedPct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${earnedPct}%` }} />
                      </div>
                      <span className="text-xs text-gray-400">{earnedPct}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Recent Journal Entries */}
        {taskSummary?.recentEntries?.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Points Earned</h3>
            <div className="space-y-2">
              {taskSummary.recentEntries.map((e: any, i: number) => (
                <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs shrink-0">{(e.userName || '?')[0]}</div>
                    <span className="text-gray-700 font-medium">{e.userName}</span>
                    <span className="text-gray-400 truncate">{e.reason}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-semibold text-green-600">+{e.points} SP</span>
                    <span className="text-gray-400">{new Date(e.earnedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </>
      )}
    </div>
  );
};

export default Leaderboard;
